import {
  Box,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import WorkHistoryRoundedIcon from "@mui/icons-material/WorkHistoryRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import { alpha } from "@mui/material/styles";

import LoginButton from "../component/LoginButton";
import theme, { designTokens } from "../theme";

type Props = {
  processing?: boolean;
};

function Login({ processing = false }: Props) {
  return (
      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          px: 2,
          py: 4,
          bgcolor: designTokens.surface.page,
          backgroundImage: `
            radial-gradient(
              circle at 15% 20%,
              ${alpha(theme.palette.primary.main, 0.10)} 0%,
              transparent 34%
            ),
            radial-gradient(
              circle at 85% 80%,
              ${alpha(theme.palette.info.main, 0.06)} 0%,
              transparent 32%
            ),
            linear-gradient(
              145deg,
              #F8FAFC 0%,
              #F4F6F8 52%,
              #EEF2F6 100%
            )
          `,
        }}
      >
        {/* 背景グリッド */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            opacity: 0.42,
            backgroundImage: `
              linear-gradient(
                ${alpha("#64748B", 0.06)} 1px,
                transparent 1px
              ),
              linear-gradient(
                90deg,
                ${alpha("#64748B", 0.06)} 1px,
                transparent 1px
              )
            `,
            backgroundSize: "42px 42px",
            maskImage:
              "radial-gradient(circle at center, black 10%, transparent 75%)",
            WebkitMaskImage:
              "radial-gradient(circle at center, black 10%, transparent 75%)",
          }}
        />

        {/* 背景装飾 */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "-180px",
            right: "-150px",
            width: 460,
            height: 460,
            borderRadius: "50%",
            border: `1px solid ${alpha(
              theme.palette.primary.main,
              0.12
            )}`,
            boxShadow: `
              0 0 0 55px ${alpha(theme.palette.primary.main, 0.035)},
              0 0 0 110px ${alpha(theme.palette.primary.main, 0.022)}
            `,
            pointerEvents: "none",
          }}
        />

        <Box
          aria-hidden
          sx={{
            position: "absolute",
            bottom: "-210px",
            left: "-160px",
            width: 430,
            height: 430,
            borderRadius: "50%",
            background: `radial-gradient(
              circle,
              ${alpha(theme.palette.primary.light, 0.1)} 0%,
              transparent 68%
            )`,
            pointerEvents: "none",
          }}
        />

        <Paper
          elevation={0}
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            maxWidth: 430,
            overflow: "hidden",
            borderRadius: 4,
            color: designTokens.text.primary,
            bgcolor: designTokens.surface.paper,
            border: `1px solid ${designTokens.border.default}`,
            boxShadow: `
              0 24px 64px rgba(15,23,42,0.14),
              0 8px 24px rgba(15,23,42,0.08)
            `,
          }}
        >
          {/* 上部アクセント */}
          <Box
            sx={{
              height: 5,
              background: `linear-gradient(
                90deg,
                ${theme.palette.primary.dark} 0%,
                ${theme.palette.primary.main} 48%,
                ${theme.palette.primary.light} 100%
              )`,
              boxShadow: `0 0 18px ${alpha(
                theme.palette.primary.main,
                0.24
              )}`,
            }}
          />

          <Box
            sx={{
              px: {
                xs: 3,
                sm: 5,
              },
              pt: {
                xs: 4,
                sm: 5,
              },
              pb: {
                xs: 3.5,
                sm: 4.5,
              },
              textAlign: "center",
            }}
          >
            {/* アイコン */}
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 86,
                height: 86,
                mb: 3,
                borderRadius: 3.5,
                color: designTokens.brand.dark,
                background: `linear-gradient(
                  145deg,
                  ${alpha(theme.palette.primary.main, 0.17)} 0%,
                  ${alpha(theme.palette.primary.main, 0.055)} 100%
                )`,
                border: `1px solid ${alpha(
                  theme.palette.primary.main,
                  0.26
                )}`,
                boxShadow: `
                  0 14px 34px ${alpha(
                    theme.palette.primary.main,
                    0.12
                  )},
                  inset 0 1px 0 ${alpha("#ffffff", 0.70)}
                `,

                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: -7,
                  borderRadius: 4.5,
                  border: `1px solid ${alpha(
                    theme.palette.primary.main,
                    0.07
                  )}`,
                },
              }}
            >
              <WorkHistoryRoundedIcon
                sx={{
                  fontSize: 45,
                }}
              />
            </Box>

            <Typography
              component="p"
              sx={{
                mb: 1,
                color: designTokens.brand.dark,
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
              }}
            >
              Sales Information Management
            </Typography>

            <Typography
              variant="h5"
              component="h1"
              sx={{
                color: designTokens.text.primary,
                fontWeight: 700,
                lineHeight: 1.45,
                letterSpacing: "0.035em",
              }}
            >
              営業情報管理システム
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 1.5,
                color: designTokens.text.secondary,
                lineHeight: 1.8,
              }}
            >
              アカウント認証を行い、
              <br />
              システムへログインしてください。
            </Typography>

            <Box
              sx={{
                width: 54,
                height: 1,
                mx: "auto",
                my: 3.5,
                background: `linear-gradient(
                  90deg,
                  transparent,
                  ${alpha(theme.palette.primary.main, 0.8)},
                  transparent
                )`,
              }}
            />

            {processing ? (
              <Box
                sx={{
                  minHeight: 72,
                  py: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <CircularProgress
                  size={34}
                  thickness={4}
                  sx={{
                    color: "primary.main",
                  }}
                />

                <Typography
                  variant="body2"
                  sx={{
                    color: designTokens.text.secondary,
                    fontWeight: 500,
                  }}
                >
                  ログイン情報を確認しています。
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  width: "100%",

                  "& > *": {
                    width: "100%",
                  },

                  "& button": {
                    width: "100%",
                    minHeight: 52,
                    borderRadius: 2.5,
                    fontSize: "0.95rem",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    boxShadow: `0 10px 26px ${alpha(
                      theme.palette.primary.main,
                      0.18
                    )}`,
                    transition:
                      "transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease",

                    "&:hover": {
                      color: "#17210B",
                      transform: "translateY(-2px)",
                      filter: "brightness(1.04)",
                      boxShadow: `0 15px 34px ${alpha(
                        theme.palette.primary.main,
                        0.25
                      )}`,
                    },

                    "&:active": {
                      transform: "translateY(0)",
                    },
                  },
                }}
              >
                <LoginButton />
              </Box>
            )}

            <Box
              sx={{
                mt: 4,
                pt: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                borderTop: `1px solid ${designTokens.border.default}`,
              }}
            >
              <LockRoundedIcon
                sx={{
                  fontSize: 15,
                  color: designTokens.brand.dark,
                }}
              />

              <Typography
                variant="caption"
                sx={{
                  color: designTokens.text.secondary,
                  letterSpacing: "0.025em",
                }}
              >
                セキュアな認証で保護されています
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            zIndex: 1,
            bottom: {
              xs: 12,
              sm: 22,
            },
            color: designTokens.text.muted,
            letterSpacing: "0.06em",
          }}
        >
          © M-AND-T
        </Typography>
      </Box>
  );
}

export default Login;